/* eslint-disable react/prop-types */
/* eslint-disable prettier/prettier */
import { useState, useEffect } from "react";
import axios from "axios";
import API_ENDPOINTS from "config/api";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Divider from "@mui/material/Divider";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

// Material Dashboard 2 React examples
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";
import HorizontalBarChart from "examples/Charts/BarCharts/HorizontalBarChart";
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";

// Utilidades de exportación
import { exportDashboardToPDF, exportToExcel, handlePrint as printPage } from "utils/exportHelpers";
import PieChart from "examples/Charts/PieChart";

export default function IndicadoresGestion() {
  const [categoria, setCategoria] = useState("financieros");
  const [periodo, setPeriodo] = useState("mes");

  // Estados para datos de API
  const [kpisFinancieros, setKpisFinancieros] = useState([]);
  const [kpisOperativos, setKpisOperativos] = useState([]);
  const [kpisClientes, setKpisClientes] = useState([]);
  const [kpisRRHH, setKpisRRHH] = useState([]);

  // Charts State Initialization
  const [evolucionRatios, setEvolucionRatios] = useState({ labels: [], datasets: [{ label: "Ratio", data: [] }] });
  const [distribucionCostos, setDistribucionCostos] = useState({ labels: [], datasets: [{ label: "Costos", backgroundColors: [], data: [] }] });
  const [eficienciaPorArea, setEficienciaPorArea] = useState({ labels: [], datasets: [{ label: "Eficiencia", data: [] }] });
  const [productividadEmpleado, setProductividadEmpleado] = useState({ labels: [], datasets: [{ label: "Productividad", data: [] }] });
  const [satisfaccionCliente, setSatisfaccionCliente] = useState({ labels: [], datasets: [{ label: "Satisfacción", data: [] }] });
  const [indicadoresDetallados, setIndicadoresDetallados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Obtener datos del API
  useEffect(() => {
    const fetchDatos = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(API_ENDPOINTS.REPORTES.INDICADORES, {
          params: { categoria, periodo },
        });

        const data = response.data;
        const formatCurrency = (value) => {
          const formatted = new Intl.NumberFormat("es-MX", {
            notation: "compact",
            compactDisplay: "short",
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          }).format(value);
          return `$${formatted}`;
        };

        // Transformar KPIs según categoría
        const transformKpis = (kpis) =>
          kpis.map((kpi) => ({
            color:
              kpi.title.includes("ROE") ||
                kpi.title.includes("Retención") ||
                kpi.title.includes("Eficiencia")
                ? "success"
                : kpi.title.includes("ROA") ||
                  kpi.title.includes("Operacional") ||
                  kpi.title.includes("Rotación")
                  ? "info"
                  : kpi.title.includes("ROI") ||
                    kpi.title.includes("NPS") ||
                    kpi.title.includes("Adquisición")
                    ? "primary"
                    : kpi.title.includes("EBITDA") ||
                      kpi.title.includes("Entregas") ||
                      kpi.title.includes("LTV") ||
                      kpi.title.includes("Productividad")
                      ? "warning"
                      : "error",
            icon:
              kpi.title.includes("ROE") || kpi.title.includes("Adquisición")
                ? "trending_up"
                : kpi.title.includes("ROA")
                  ? "account_balance"
                  : kpi.title.includes("ROI") || kpi.title.includes("NPS")
                    ? "analytics"
                    : kpi.title.includes("EBITDA") || kpi.title.includes("LTV")
                      ? "payments"
                      : kpi.title.includes("Eficiencia") || kpi.title.includes("Productividad")
                        ? "precision_manufacturing"
                        : kpi.title.includes("Rotación") && kpi.title.includes("Inventario")
                          ? "inventory"
                          : kpi.title.includes("Rotación")
                            ? "groups"
                            : kpi.title.includes("Entregas")
                              ? "local_shipping"
                              : kpi.title.includes("Devoluciones") || kpi.title.includes("Ausentismo")
                                ? "assignment_return"
                                : kpi.title.includes("Retención") || kpi.title.includes("Satisfacción Laboral")
                                  ? "loyalty"
                                  : kpi.title.includes("Satisfacción")
                                    ? "star"
                                    : "person_add",
            title: kpi.title,
            count:
              kpi.suffix === "%" || kpi.suffix === "x" || kpi.suffix === ""
                ? `${(kpi.value || 0).toFixed(1)}${kpi.suffix}`
                : kpi.suffix === "/año"
                  ? formatCurrency(kpi.value) + kpi.suffix
                  : formatCurrency(kpi.value),
            percentage: {
              color: kpi.change >= 0 ? "success" : "error",
              amount: kpi.change >= 0 ? `+${(kpi.change || 0).toFixed(1)}%` : `${(kpi.change || 0).toFixed(1)}%`,
              label:
                kpi.suffix === "%"
                  ? "vs período anterior"
                  : kpi.suffix === "x"
                    ? `cada ${Math.floor(30 / (kpi.value || 1))} días`
                    : kpi.suffix === "/año"
                      ? "vs año anterior"
                      : kpi.title.includes("NPS")
                        ? "Net Promoter Score"
                        : kpi.title.includes("Adquisición")
                          ? "nuevos clientes/mes"
                          : kpi.title.includes("Retención")
                            ? "clientes recurrentes"
                            : kpi.title.includes("LTV")
                              ? "valor cliente"
                              : kpi.title.includes("Rotación") && categoria === "rrhh"
                                ? "reducción"
                                : kpi.title.includes("Ausentismo")
                                  ? "mejora"
                                  : kpi.title.includes("Satisfacción Laboral")
                                    ? "clima laboral"
                                    : kpi.title.includes("Eficiencia")
                                      ? "vs mes anterior"
                                      : kpi.title.includes("Entregas")
                                        ? "objetivo: 95%"
                                        : kpi.title.includes("Devoluciones")
                                          ? "reducción"
                                          : "vs período anterior",
            },
          }));

        if (categoria === "financieros") {
          setKpisFinancieros(transformKpis(data.kpis || []));
        } else if (categoria === "operativos") {
          setKpisOperativos(transformKpis(data.kpis || []));
        } else if (categoria === "clientes") {
          setKpisClientes(transformKpis(data.kpis || []));
        } else {
          setKpisRRHH(transformKpis(data.kpis || []));
        }
      } catch (error) {
        console.error("Error al cargar indicadores:", error);
        setError("Error al cargar los datos");
      } finally {
        setLoading(false);
      }
    };

    fetchDatos();
  }, [categoria, periodo]);

  const handleExportPDF = async () => {
    await exportDashboardToPDF(
      "indicadores-gestion-content",
      `indicadores-gestion-${categoria}-${new Date().getTime()}.pdf`
    );
  };

  const handleExportExcel = () => {
    const kpis = getKPIs();
    const data = kpis.map((kpi) => ({
      Indicador: kpi.title,
      Valor: kpi.count,
      Meta: kpi.meta,
      Cumplimiento: kpi.cumplimiento,
      Estado: kpi.percentage.label,
    }));

    exportToExcel(
      data,
      `Indicadores ${categoria}`,
      `indicadores-gestion-${categoria}-${new Date().getTime()}.xlsx`
    );
  };

  const handlePrintReport = () => {
    printPage();
  };

  const getKPIs = () => {
    if (categoria === "financieros") return kpisFinancieros;
    if (categoria === "operativos") return kpisOperativos;
    if (categoria === "clientes") return kpisClientes;
    return kpisRRHH;
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case "alto":
        return "success";
      case "normal":
        return "info";
      case "bajo":
        return "warning";
      default:
        return "dark";
    }
  };

  return (
    <MDBox id="indicadores-gestion-content">
      {/* Filtros y Controles */}
      <Card sx={{ mb: 3 }}>
        <MDBox p={3}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <MDInput
                select
                label="Categoría de Indicadores"
                value={categoria}
                onChange={(e) => setCategoria(e.target.value)}
                fullWidth
              >
                <MenuItem value="financieros">Indicadores Financieros</MenuItem>
                <MenuItem value="operativos">Indicadores Operativos</MenuItem>
                <MenuItem value="clientes">Indicadores de Clientes</MenuItem>
                <MenuItem value="rrhh">Indicadores de RRHH</MenuItem>
              </MDInput>
            </Grid>

            <Grid item xs={12} md={4}>
              <MDInput
                select
                label="Período"
                value={periodo}
                onChange={(e) => setPeriodo(e.target.value)}
                fullWidth
              >
                <MenuItem value="mes">Este Mes</MenuItem>
                <MenuItem value="trimestre">Este Trimestre</MenuItem>
                <MenuItem value="semestre">Este Semestre</MenuItem>
                <MenuItem value="anio">Este Año</MenuItem>
              </MDInput>
            </Grid>

            <Grid item xs={12} md={4}>
              <MDBox display="flex" gap={1}>
                <MDButton variant="outlined" color="dark" size="small" onClick={handleExportPDF}>
                  <Icon>picture_as_pdf</Icon>&nbsp;PDF
                </MDButton>
                <MDButton
                  variant="outlined"
                  color="success"
                  size="small"
                  onClick={handleExportExcel}
                >
                  <Icon>table_chart</Icon>&nbsp;Excel
                </MDButton>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </Card>

      {/* KPIs Principales */}
      <Grid container spacing={3} mb={3}>
        {getKPIs().map((kpi, index) => (
          <Grid item xs={12} md={6} lg={3} key={index}>
            <ComplexStatisticsCard
              color={kpi.color}
              icon={kpi.icon}
              title={kpi.title}
              count={kpi.count}
              percentage={kpi.percentage}
            />
          </Grid>
        ))}
      </Grid>

      {/* Gráficas */}
      <Grid container spacing={3} mb={3}>
        {categoria === "financieros" && (
          <>
            <Grid item xs={12} lg={8}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="h6" fontWeight="medium" mb={2}>
                    Evolución de Ratios de Rentabilidad
                  </MDTypography>
                  <DefaultLineChart chart={evolucionRatios} height="300px" />
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} lg={4}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="h6" fontWeight="medium" mb={2}>
                    Distribución de Costos
                  </MDTypography>
                  <PieChart chart={distribucionCostos} height="300px" />
                </MDBox>
              </Card>
            </Grid>
          </>
        )}

        {categoria === "operativos" && (
          <>
            <Grid item xs={12} lg={6}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="h6" fontWeight="medium" mb={2}>
                    Eficiencia por Área
                  </MDTypography>
                  <HorizontalBarChart chart={eficienciaPorArea} height="300px" />
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="h6" fontWeight="medium" mb={2}>
                    Productividad por Empleado
                  </MDTypography>
                  <VerticalBarChart chart={productividadEmpleado} height="300px" />
                </MDBox>
              </Card>
            </Grid>
          </>
        )}

        {categoria === "clientes" && (
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>
                  Evolución de Satisfacción del Cliente
                </MDTypography>
                <DefaultLineChart chart={satisfaccionCliente} height="300px" />
              </MDBox>
            </Card>
          </Grid>
        )}

        {categoria === "rrhh" && (
          <>
            <Grid item xs={12} lg={6}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="h6" fontWeight="medium" mb={2}>
                    Productividad por Empleado (Mensual)
                  </MDTypography>
                  <VerticalBarChart chart={productividadEmpleado} height="300px" />
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="h6" fontWeight="medium" mb={2}>
                    Distribución de Costos de Personal
                  </MDTypography>
                  <PieChart chart={distribucionCostos} height="300px" />
                </MDBox>
              </Card>
            </Grid>
          </>
        )}
      </Grid>

      {/* Tabla de Indicadores Detallados */}
      <Card>
        <MDBox p={3}>
          <MDTypography variant="h6" fontWeight="medium" mb={2}>
            Indicadores Detallados - {categoria.charAt(0).toUpperCase() + categoria.slice(1)}
          </MDTypography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Indicador</strong>
                  </TableCell>
                  <TableCell align="center">
                    <strong>Valor Actual</strong>
                  </TableCell>
                  <TableCell align="center">
                    <strong>Objetivo</strong>
                  </TableCell>
                  <TableCell align="center">
                    <strong>Estado</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Fórmula</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {indicadoresDetallados.map((ind, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <MDTypography variant="button" fontWeight="medium">
                        {ind.indicador}
                      </MDTypography>
                    </TableCell>
                    <TableCell align="center">
                      <MDTypography variant="button" fontWeight="bold">
                        {ind.valor}
                      </MDTypography>
                    </TableCell>
                    <TableCell align="center">
                      <MDTypography variant="caption" color="text">
                        {ind.objetivo}
                      </MDTypography>
                    </TableCell>
                    <TableCell align="center">
                      <MDBox
                        px={1.5}
                        py={0.5}
                        borderRadius="md"
                        display="inline-block"
                        sx={{
                          backgroundColor: ({ palette }) =>
                            palette[getEstadoColor(ind.estado)].main,
                          color: "white",
                        }}
                      >
                        <MDTypography variant="caption" fontWeight="medium" color="white">
                          {ind.estado.toUpperCase()}
                        </MDTypography>
                      </MDBox>
                    </TableCell>
                    <TableCell>
                      <MDTypography variant="caption" color="text">
                        {ind.formula}
                      </MDTypography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </MDBox>
      </Card>

      {/* Resumen de Cumplimiento */}
      <Card sx={{ mt: 3 }}>
        <MDBox p={3}>
          <MDTypography variant="h6" fontWeight="medium" mb={2}>
            Resumen de Cumplimiento de Objetivos
          </MDTypography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <MDBox textAlign="center">
                <MDTypography variant="h1" fontWeight="bold" color="success">
                  85%
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  Indicadores Sobre Objetivo
                </MDTypography>
              </MDBox>
            </Grid>
            <Grid item xs={12} md={4}>
              <MDBox textAlign="center">
                <MDTypography variant="h1" fontWeight="bold" color="info">
                  12%
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  Indicadores en Objetivo
                </MDTypography>
              </MDBox>
            </Grid>
            <Grid item xs={12} md={4}>
              <MDBox textAlign="center">
                <MDTypography variant="h1" fontWeight="bold" color="warning">
                  3%
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  Indicadores Bajo Objetivo
                </MDTypography>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </Card>
    </MDBox>
  );
}
